import {
  Box,
  Button,
  Flex,
  FlexProps,
  Heading,
  HStack,
  ListItem,
  Spacer,
  Tag,
  TagProps,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import React from "react";
import { RiArrowDownLine, RiArrowUpLine } from "react-icons/ri";
import { useNavigate, useParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { NotFound } from "src/components/Feedback/NotFound";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { LearnMore } from "src/components/Links/LearnMore";
import { TipTag } from "src/components/TipTag";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { OrgAdmin } from "src/services/url";
import * as GQL from "src/types/graphql";
import {
  BinConfig,
  PriorityGroup,
  PriorityTagGroup,
  SortField,
  TagLiteral,
} from "src/types/matchConfig";
import { BinConfig as BigConfigV1 } from "src/types/priorityTemplate";
import { toTuple } from "src/types/remoteData";
import { GET_SCHOOL_BY_ID } from "../schools/graphql/queries";
import { GET_BIN_CONFIGURATION_BY_ID } from "./graphql/queries";

export function ViewMatchRule() {
  const navigate = useNavigate();
  const { schoolId = "", binConfigId = "" } = useParams();
  const organization = useOrganization();

  const goConfigure = () =>
    navigate(
      organization.map((org) => OrgAdmin.Match.configure(org)).withDefault("#")
    );

  const { remoteData: schoolRemoteData } = useRemoteDataQuery<
    GQL.GetSchool,
    GQL.GetSchoolVariables
  >(GET_SCHOOL_BY_ID, {
    variables: { id: schoolId },
    skip: !schoolId,
  });
  const { remoteData: binRemoteData } = useRemoteDataQuery<
    GQL.GetBinConfigurationById,
    GQL.GetBinConfigurationByIdVariables
  >(GET_BIN_CONFIGURATION_BY_ID, {
    variables: { binConfigId },
    skip: !binConfigId,
  });

  if (!schoolId || !binConfigId) return <GenericError />;

  return (
    <GQLRemoteDataView remoteData={toTuple(schoolRemoteData, binRemoteData)}>
      {([schoolData, binData]) => {
        if (!schoolData.school_by_pk || !binData.bin_config_by_pk) {
          return <NotFound />;
        }
        const binConfig = binConfigFromRow(binData.bin_config_by_pk);

        return (
          <Flex direction="column" gap={6} height="100%">
            <Heading as="h1">View {schoolData.school_by_pk.name} rules</Heading>

            <Flex align="flex-start" gap={2} w="100%">
              <TipTag />
              <Text>
                Priority groups rank students by which tags they match. Higher
                groups are placed first in the lottery, and any subsequent
                sorting order students within a group. Read more{" "}
                <LearnMore article="44754020584845-How-Avela-Match-Runs-Lotteries">
                  about lotteries
                </LearnMore>
                .
              </Text>
            </Flex>

            <Flex direction="column" gap={1} pl={14}>
              <HStack spacing={2} align="center">
                <RuleTag literal="Tag" mx="1px" />
                <Text>Student&apos;s response must have this tag</Text>
              </HStack>
              <HStack spacing={2} align="center">
                <RuleTag literal={{ not: "Tag" }} />
                <Text>Student&apos;s response must not have this tag</Text>
              </HStack>
            </Flex>

            {!binConfig ? (
              <GenericError message="Invalid bin configuration." />
            ) : (
              <BinConfigView binConfig={binConfig} />
            )}

            <Spacer />
            <AdminFormButtons justifyContent="space-between">
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={goConfigure}
              >
                Back
              </Button>
              <Button onClick={goConfigure}>Done</Button>
            </AdminFormButtons>
          </Flex>
        );
      }}
    </GQLRemoteDataView>
  );
}

function binConfigFromRow(
  row: GQL.GetBinConfigurationById_bin_config_by_pk
): BinConfig | null {
  if (!row.parameters || typeof row.parameters !== "object") return null;
  const params = row.parameters as BinConfig | BigConfigV1;

  // Convert legacy priority groups to new format.
  const priorityGroups = (params.priorityGroups ?? []).map((group) =>
    Array.isArray(group)
      ? { tagGroups: group.map((tagGroup) => ({ tags: tagGroup })) }
      : group
  );

  return {
    name: row.name ?? params.name,
    priorityGroups: priorityGroups,
    sortFields: params.sortFields ?? [],
    options: params.options,
  };
}

function sortFieldDisplayName(
  field: GQL.priority_template_sort_field_enum
): string {
  switch (field) {
    case GQL.priority_template_sort_field_enum.PriorityGroup:
      return "priority group";
    case GQL.priority_template_sort_field_enum.LotteryNumber:
      return "lottery number";
    case GQL.priority_template_sort_field_enum.LotteryOrder:
      return "tiebreaker";
    case GQL.priority_template_sort_field_enum.LotteryPosition:
      return "lottery position";
    case GQL.priority_template_sort_field_enum.AppSubmissionTime:
      return "form submission time";
    case GQL.priority_template_sort_field_enum.SchoolSubmissionTime:
      return "school submission time";
    case GQL.priority_template_sort_field_enum.WaitlistedTime:
      return "waitlisted time";
    default:
      return field;
  }
}

function BorderBox(props: React.PropsWithChildren<FlexProps>) {
  return (
    <Flex
      gap={2.5}
      p={3}
      borderWidth="1px"
      borderStyle="solid"
      borderColor="gray.300"
      borderRadius="md"
      {...props}
    />
  );
}

function SortFieldCard({
  index,
  sortField,
}: {
  index: number;
  sortField: SortField;
}) {
  const isAscending =
    sortField.order === GQL.priority_template_sort_order_enum.asc;
  return (
    <BorderBox ml={12 * index}>
      <Box color="gray.500" flexShrink={0}>
        {isAscending ? (
          <RiArrowUpLine size={24} />
        ) : (
          <RiArrowDownLine size={24} />
        )}
      </Box>
      <Text>
        Sort:{" "}
        <Text as="span" fontWeight="bold">
          {sortFieldDisplayName(sortField.field)}
        </Text>
        {" — "}
        {isAscending ? "ascending order" : "descending order"}
      </Text>
    </BorderBox>
  );
}

function RuleTag({ literal, ...tagProps }: { literal: TagLiteral } & TagProps) {
  const isNegated = typeof literal === "object" && "not" in literal;
  return (
    <Tag
      size="sm"
      variant={isNegated ? "tagNegation" : "tag"}
      verticalAlign="text-top"
      {...tagProps}
    >
      {isNegated ? literal.not : literal}
    </Tag>
  );
}

function TagGroupRow({ tagGroup }: { tagGroup: PriorityTagGroup }) {
  return (
    <Flex align="center" flexWrap="wrap" gap={2}>
      {tagGroup.tags.length > 0 ? (
        tagGroup.tags.map((literal, i) => (
          <React.Fragment key={i}>
            {i > 0 ? <Text as="span">+</Text> : null}
            <RuleTag literal={literal} />
          </React.Fragment>
        ))
      ) : (
        <Text as="span" variant="placeholder">
          Any form
        </Text>
      )}
      {tagGroup.lotteryNumbers != null && tagGroup.lotteryNumbers !== 1 ? (
        <Text fontSize="sm" color="gray.500">
          ({tagGroup.lotteryNumbers} lottery numbers)
        </Text>
      ) : null}
    </Flex>
  );
}

function OrSeparator() {
  return (
    <HStack spacing={2} alignItems="center" flexShrink={0}>
      <Box w="1.5em" h="1px" bg="gray.300" />
      <Text fontSize="sm" color="gray.500">
        OR
      </Text>
      <Box w="1.5em" h="1px" bg="gray.300" />
    </HStack>
  );
}

function PriorityGroupCard({
  index,
  group,
}: {
  index: number;
  group: PriorityGroup;
}) {
  return (
    <BorderBox>
      <Text px={3} color="gray.500" flexShrink={0}>
        {index + 1}
      </Text>
      <Flex direction="column" gap={2}>
        {group.tagGroups.map((tg, gi) => (
          <Flex key={gi} gap={2}>
            <TagGroupRow tagGroup={tg} />
            {gi < group.tagGroups.length - 1 ? <OrSeparator /> : null}
          </Flex>
        ))}
      </Flex>
    </BorderBox>
  );
}

function OptionValue({
  label,
  isDisabled,
}: {
  label: string;
  isDisabled?: boolean;
}) {
  return (
    <Text
      as="span"
      {...(isDisabled ? { variant: "placeholder" } : { fontWeight: "bold" })}
    >
      {label}
    </Text>
  );
}

function OptionsCard({ options = {} }: { options: BinConfig["options"] }) {
  return (
    <BorderBox>
      <UnorderedList>
        <ListItem>
          Dynamic sibling tag:{" "}
          {options.dynamicSiblingTag ? (
            <RuleTag literal={options.dynamicSiblingTag} />
          ) : (
            <OptionValue label="None" isDisabled />
          )}
        </ListItem>
        <ListItem>
          Prioritize tuplets:{" "}
          {options.prioritizeTuplets ? (
            <OptionValue label="Yes" />
          ) : (
            <OptionValue label="No" isDisabled />
          )}
        </ListItem>
      </UnorderedList>
    </BorderBox>
  );
}

function BinConfigView({ binConfig }: { binConfig: BinConfig }) {
  return (
    <>
      <Flex direction="column" gap={2}>
        <Text>Sorting</Text>
        {binConfig.sortFields.map((sf, i) => (
          <SortFieldCard key={i} index={i} sortField={sf} />
        ))}
      </Flex>

      <Flex direction="column" gap={2}>
        <Text>Priority groups</Text>
        {binConfig.priorityGroups.map((group, i) => (
          <PriorityGroupCard key={i} index={i} group={group} />
        ))}
      </Flex>

      <Flex direction="column" gap={2}>
        <Text>Options</Text>
        <OptionsCard options={binConfig.options} />
      </Flex>
    </>
  );
}
